import { useEffect, useRef, useState } from "react"
import { useDropzone } from "react-dropzone"
import { FiUploadCloud, FiX } from "react-icons/fi"

export default function Upload({
  name,
  label,
  register,
  setValue,
  errors,
  video = false,
  viewData = null,
  editData = null,
  required = true,
}) {
  const [selectedFile, setSelectedFile] = useState(null)
  const [previewSource, setPreviewSource] = useState(viewData || editData || "")
  const inputRef = useRef(null)

  const onDrop = (acceptedFiles) => {
    const file = acceptedFiles[0]
    if (!file) return
    previewFile(file)
    setSelectedFile(file)
  }

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: !video
      ? { "image/*": [".jpeg", ".jpg", ".png"] }
      : { "video/*": [".mp4", ".webm", ".mov"] },
    onDrop,
    maxFiles: 1,
  })

  const previewFile = (file) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onloadend = () => setPreviewSource(reader.result)
  }

  useEffect(() => {
    register(name, { required })
  }, [register, name, required])

  useEffect(() => {
    setValue(name, selectedFile)
  }, [selectedFile, setValue, name])

  const handleClear = () => {
    setPreviewSource("")
    setSelectedFile(null)
    setValue(name, null)
  }

  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <label className="text-sm font-medium text-richblack-5" htmlFor={name}>
          {label} {!viewData && required && <sup className="text-pink-200">*</sup>}
        </label>
      )}

      <div
        className={`relative min-h-[160px] cursor-pointer overflow-hidden rounded-xl border-2 border-dashed transition-colors sm:min-h-[200px] ${
          isDragActive
            ? "border-yellow-50 bg-richblack-600"
            : "border-richblack-500 bg-richblack-700 hover:border-richblack-300"
        }`}
      >
        {previewSource ? (
          <div className="flex w-full flex-col items-center p-3 sm:p-4">
            {!video ? (
              <img
                src={previewSource}
                alt="Preview"
                className="max-h-[200px] w-full rounded-lg object-cover sm:max-h-[280px]"
              />
            ) : (
              <video
                src={previewSource}
                controls
                playsInline
                className="w-full rounded-lg bg-black"
                style={{ display: "block", maxHeight: 240 }}
              />
            )}
            {!viewData && (
              <button
                type="button"
                onClick={handleClear}
                className="mt-3 flex items-center gap-1.5 rounded-lg border border-richblack-500 px-3 py-1.5 text-xs font-medium text-richblack-300 transition-colors hover:border-pink-400 hover:text-pink-300"
              >
                <FiX size={13} /> Remove &amp; replace
              </button>
            )}
          </div>
        ) : (
          <div
            className="flex h-full w-full flex-col items-center justify-center gap-3 p-6 sm:p-8"
            {...getRootProps()}
          >
            <input {...getInputProps()} ref={inputRef} />
            <div className="grid h-12 w-12 place-items-center rounded-full bg-richblack-800 sm:h-14 sm:w-14">
              <FiUploadCloud className="text-2xl text-yellow-50 sm:text-3xl" />
            </div>
            <div className="text-center">
              <p className="text-sm text-richblack-200">
                Drag &amp; drop {!video ? "an image" : "a video"}, or{" "}
                <span
                  className="font-semibold text-yellow-50 underline"
                  onClick={() => inputRef.current?.click()}
                >
                  Browse
                </span>
              </p>
              <p className="mt-1 text-xs text-richblack-500">
                {!video
                  ? "JPEG, JPG, PNG · Recommended 1024×576"
                  : "MP4, WebM, MOV · 16:9 ratio"}
              </p>
            </div>
          </div>
        )}
      </div>

      {errors[name] && (
        <p className="flex items-center gap-1 text-xs text-pink-200">
          <span>⚠</span> {label || "This field"} is required
        </p>
      )}
    </div>
  )
}
